import { staticFile } from '@common/utils'
import { useEffect, useMemo, useState } from 'react'

type OptionsType = {
  playOnMount?: boolean
  pathToAudioFile: string
  /** a value from 0 to 1. Default: 0.1 */
  volumn?: number
  /** Default: true */
  loop?: boolean
}

const useBackgroundMusic = ({
  playOnMount = false,
  pathToAudioFile,
  volumn = 0.1,
  loop = true,
}: OptionsType) => {
  const [isBackgroundMusicOn, setIsBackgroundMusicOn] = useState(playOnMount)

  const audioInstance =
    typeof Audio !== 'undefined' && new Audio(staticFile(pathToAudioFile))
  const backgroundMusicSound = useMemo(() => audioInstance, [])

  const playBackgroundMusic = () => {
    if (backgroundMusicSound.currentTime === 0) setIsBackgroundMusicOn(true)
    document.removeEventListener('click', playBackgroundMusic)
  }

  const handleBackgroundMusic = async () => {
    backgroundMusicSound.volume = volumn
    backgroundMusicSound.loop = loop

    try {
      if (isBackgroundMusicOn) {
        await backgroundMusicSound.play()
        // Execution reached here, means no need to play music when user interacts with the
        // document as it is already playing.
        document.removeEventListener('click', playBackgroundMusic)
      } else {
        backgroundMusicSound.pause()
        backgroundMusicSound.currentTime = 0
      }
    } catch (error) {
      console.error(error)
      setIsBackgroundMusicOn(false)
    }
  }

  // Handles pausing and playing background music depending on the state
  useEffect(() => {
    handleBackgroundMusic()
    return () => backgroundMusicSound.pause()
  }, [isBackgroundMusicOn])

  // Due to chrome's autoplay policy, autoplay is blocked untill user interacts with the page.
  // Hence playing the audio once user clicks on the page.
  useEffect(() => {
    playOnMount && document.addEventListener('click', playBackgroundMusic)
    return () => document.removeEventListener('click', playBackgroundMusic)
  }, [])

  return {
    isBackgroundMusicOn,
    setIsBackgroundMusicOn,
  }
}

export default useBackgroundMusic
